import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useId, useLayoutEffect, useState } from "react";
import "./index.css";
import type { ISwitchProps } from "./types";

export const Switch = ({
  checked,
  onChange,
  label,
  description,
  accessory,
  togglePosition = "start",
  disabled = false,
  className,
  size = "md",
  id,
  style,
  focusRingTarget = "track",
  ...inputProps
}: ISwitchProps) => {
  const fallbackId = useId();
  const switchId = id || fallbackId;

  const [pressed, setPressed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useLayoutEffect(() => {
    setTimeout(() => setIsMounted(true), 100);
  }, []);

  useEffect(() => {
    if (!pressed) return;
    const off = () => setPressed(false);
    window.addEventListener("pointerup", off, { once: true });
    window.addEventListener("pointercancel", off, { once: true });
    return () => {
      window.removeEventListener("pointerup", off);
      window.removeEventListener("pointercancel", off);
    };
  }, [pressed]);

  const rootClassName = [
    "nuco-switch",
    "nuco-color-brand",
    `nuco-switch--${size}`,
    checked && "nuco-switch--checked",
    togglePosition === "end" && "nuco-switch--reverse",
    disabled && "nuco-switch--disabled",
    focusRingTarget === "container" && "nuco-switch--container-focus",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  const onPointerDownRoot = () => {
    if (!disabled) setPressed(true);
  };

  const onPointerUpRoot = () => setPressed(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") setPressed(true);
    inputProps.onKeyDown?.(e);
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") setPressed(false);
    inputProps.onKeyUp?.(e);
  };

  const descriptionId = description ? `${switchId}__desc` : undefined;
  const accessoryId = accessory ? `${switchId}-accessory` : undefined;
  const describedBy =
    [inputProps["aria-describedby"], descriptionId, accessoryId]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <label
      className={rootClassName}
      htmlFor={switchId}
      data-pressed={pressed ? "true" : "false"}
      data-mounted={isMounted ? "true" : "false"}
      data-focusring={focusRingTarget}
      onPointerDown={onPointerDownRoot}
      onPointerUp={onPointerUpRoot}
      onPointerCancel={onPointerUpRoot}
      onPointerLeave={onPointerUpRoot}
      style={style}
    >
      <div className="nuco-switch__input-wrapper">
        <input
          {...inputProps}
          id={switchId}
          type="checkbox"
          role="switch"
          className="nuco-switch__input"
          tabIndex={disabled ? -1 : 0}
          checked={checked}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-label={
            !label && typeof inputProps["aria-label"] === "string"
              ? inputProps["aria-label"]
              : undefined
          }
        />

        <div className="nuco-switch__toggle">
          <div className="nuco-switch__toggle-inner" />
        </div>
      </div>

      {(label || description) && (
        <div className="nuco-switch__content">
          {label && (
            <div className="nuco-switch__label-wrapper">
              <span className="nuco-switch__label nuco-text nuco-text--md">
                {label}
              </span>
              {accessory && (
                <div
                  id={accessoryId}
                  className="nuco-switch__accessory"
                  role="group"
                >
                  {accessory}
                </div>
              )}
            </div>
          )}
          {description && (
            <div
              id={descriptionId}
              className="nuco-switch__description nuco-text nuco-text--md"
            >
              {description}
            </div>
          )}
        </div>
      )}
    </label>
  );
};
